import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, message, Modal, Typography } from 'antd';

const getPaymentUrl = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const usePaymentRedirect = () => {
  const [paymentUrl, setPaymentUrl] = useState<string>();
  const savedUrl = useRef<string | undefined>(undefined);
  const mountedRef = useRef(true);
  const [messageApi, messageHolder] = message.useMessage();

  const reset = useCallback(() => {
    savedUrl.current = undefined;
    if (mountedRef.current) setPaymentUrl(undefined);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      savedUrl.current = undefined;
    };
  }, []);

  const openPaymentPage = useCallback((value: unknown) => {
    if (!mountedRef.current) return false;
    const url = getPaymentUrl(value);
    if (!url) {
      reset();
      messageApi.error('支付链接无效，请重新发起支付');
      return false;
    }

    try {
      const paymentWindow = window.open(url, '_blank');
      if (paymentWindow) {
        try {
          paymentWindow.opener = null;
        } catch {
          // 支付页已打开；跨域限制不影响本次跳转结果。
        }
        reset();
        return true;
      }
    } catch {
      // 未能打开时保留当前订单链接，供用户继续支付。
    }

    if (savedUrl.current === url) {
      messageApi.warning('浏览器阻止了支付页面，请允许弹出窗口后重试');
    }
    savedUrl.current = url;
    setPaymentUrl(url);
    return false;
  }, [messageApi, reset]);

  const contextHolder = (
    <>
      {messageHolder}
      <Modal
        title="支付页面未打开"
        open={Boolean(paymentUrl)}
        onCancel={reset}
        footer={
          <>
            <Button onClick={reset}>关闭</Button>
            <Button type="primary" onClick={() => {
              if (savedUrl.current) openPaymentPage(savedUrl.current);
            }}>
              重新打开支付页
            </Button>
          </>
        }
        destroyOnHidden
      >
        <Typography.Paragraph>
          支付页面未能打开，请点击下方按钮继续当前订单的支付。
        </Typography.Paragraph>
        <Typography.Link href={paymentUrl} target="_blank" rel="noopener noreferrer">
          打开支付链接
        </Typography.Link>
      </Modal>
    </>
  );

  return { openPaymentPage, reset, contextHolder };
};

export default usePaymentRedirect;
